import React from "react";
import CarLogo from "../../assets/usacarbooking-hero.png";

const About = () => {
  const handlePhoneClick = () => {
    if (window.gtag) {
      window.gtag("config", "AW-18343552397/3FAKCIr6l9ccEI3L8apE", {
        phone_conversion_number: "1-877-991-0909",
      });
    }
  };
  return (
    <main className="w-full bg-[#f4f7fb] font-sans text-slate-800 overflow-hidden">
      {/* SECTION 1: About Hero */}
      <section className="mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-16 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="pr-0 lg:pr-8">
            <div className="flex items-center gap-2 mb-4">
              <svg
                className="w-3 h-3 text-[#e32b22]"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M10 13l-5-5 5-5v10z" />
              </svg>
              <span className="text-[#e32b22] text-xs font-bold uppercase tracking-widest">
                About USACarBooking
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-5xl font-extrabold text-[#091b3e] leading-tight mb-6">
              Making car rental reservations more convenient
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              USACarBooking is a subsidiary company powered by Travelanzo LLC,
              providing independent booking assistance for customers looking to
              arrange car rental reservations, compare vehicle categories, and
              get practical support during the request process.
            </p>
          </div>

          {/* Added mb-16 on mobile so the absolute card has space to breathe */}
          <div className="relative mt-8 lg:mt-0 mb-16 lg:mb-0">
            <div className="rounded-2xl overflow-hidden shadow-xl bg-slate-200 aspect-[4/3] w-full">
              <img
                src={CarLogo}
                alt="Dark blue car at airport"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* Centered on mobile, offset to the left on Large (lg) screens */}
            <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 lg:-bottom-12 lg:left-[-3rem] lg:translate-x-0 bg-white rounded-2xl shadow-xl p-5 sm:p-6 w-[92%] sm:w-3/4 max-w-sm flex flex-col gap-4 z-10 border border-slate-50 lg:border-none">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-[#e32b22] p-2.5 sm:p-3 rounded-lg flex-shrink-0 text-white mt-1 sm:mt-0">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-[#091b3e] font-bold text-[14px] sm:text-[15px] leading-tight mb-2">
                    Customer support without unsupported promises
                  </h3>
                  <p className="text-slate-500 text-xs leading-relaxed">
                    We do not claim to own rental vehicles or operate official
                    rental counters. Final vehicle availability, pricing, and
                    provider terms may depend on the applicable third-party
                    provider.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: What We Focus On */}
      <section className="mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 mt-8 lg:mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4 lg:pr-4">
            <div className="flex items-center gap-2 mb-4">
              <svg
                className="w-3 h-3 text-[#e32b22]"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M10 13l-5-5 5-5v10z" />
              </svg>
              <span className="text-[#e32b22] text-xs font-bold uppercase tracking-widest">
                What we focus on
              </span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-[#091b3e] leading-tight">
              Booking assistance built around clear next steps
            </h2>
          </div>

          {/* Changed to sm:grid-cols-2 lg:grid-cols-3 for better tablet scaling */}
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-4 border border-slate-100 hover:shadow-md transition-shadow">
              <div className="bg-[#091b3e] text-white p-3 rounded-lg w-12 h-12 flex items-center justify-center">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
              </div>
              <h3 className="text-[#091b3e] font-bold text-lg leading-tight">
                Independent booking assistance
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                We help customers organize rental requests while remaining
                independent from rental car brands and suppliers.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-4 border border-slate-100 hover:shadow-md transition-shadow">
              <div className="bg-[#091b3e] text-white p-3 rounded-lg w-12 h-12 flex items-center justify-center">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-[#091b3e] font-bold text-lg leading-tight">
                Simpler reservation experience
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Our static request flow keeps the essentials clear: pickup
                details, travel dates, vehicle preference, and contact
                information.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-4 border border-slate-100 hover:shadow-md transition-shadow">
              <div className="bg-[#091b3e] text-white p-3 rounded-lg w-12 h-12 flex items-center justify-center">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-[#091b3e] font-bold text-lg leading-tight">
                Travel-related support
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Customers can contact us for booking questions, itinerary
                changes, and cancellation requests where permitted.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: How We Can Help & Service Info */}
      <section className="mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          <div className="bg-[#091b3e] rounded-3xl p-6 sm:p-8 lg:p-12 text-white flex flex-col justify-center shadow-lg">
            <div className="flex items-center gap-2 mb-6 text-white/80">
              <svg
                className="w-3 h-3 text-[#e32b22]"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M10 13l-5-5 5-5v10z" />
              </svg>
              <span className="text-xs font-bold uppercase tracking-widest">
                How we can help
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-6">
              Support for reservations, changes, and rental questions
            </h2>
            <p className="text-white/80 text-base leading-relaxed">
              We help customers submit rental information and request support
              for booking assistance, itinerary changes, cancellation questions
              where permitted, and general travel-related rental needs.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <svg
                  className="w-3 h-3 text-[#e32b22]"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M10 13l-5-5 5-5v10z" />
                </svg>
                <span className="text-[#e32b22] text-xs font-bold uppercase tracking-widest">
                  Service Information
                </span>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-[#e32b22] flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <span className="text-[#091b3e] font-semibold text-sm leading-snug">
                    Vehicle options from third-party providers
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-[#e32b22] flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <span className="text-[#091b3e] font-semibold text-sm leading-snug">
                    Airport and city pickup request guidance
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-[#e32b22] flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <span className="text-[#091b3e] font-semibold text-sm leading-snug">
                    Reservation assistance by phone and email
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-[#e32b22] flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <span className="text-[#091b3e] font-semibold text-sm leading-snug">
                    Clear communication around rental enquiries
                  </span>
                </li>
              </ul>
            </div>

            <div className="space-y-3">
              <a
                href="tel:+18779910909"
                onClick={handlePhoneClick}
                className="flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
              >
                <svg
                  className="w-5 h-5 text-[#091b3e] flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span className="text-[#091b3e] font-bold text-sm">
                  1-877-991-0909
                </span>
              </a>
              <a
                href="mailto:info@USACarBooking.com"
                className="flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors break-words overflow-hidden"
              >
                <svg
                  className="w-5 h-5 text-[#091b3e] flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-[#091b3e] font-bold text-sm truncate">
                  info@USACarBooking.com
                </span>
              </a>
              <div className="flex items-start sm:items-center gap-4 p-4 border border-slate-200 rounded-xl bg-slate-50">
                <svg
                  className="w-5 h-5 text-[#091b3e] flex-shrink-0 mt-0.5 sm:mt-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="text-[#091b3e] font-bold text-sm uppercase tracking-wide break-words">
                  {/* 2730 Cedarview Road, Ottawa, Ontario, K2J7J8, Canada */}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: Disclaimer Banner */}
      <section className="mx-auto px-4 sm:px-6 lg:px-8 pb-12 lg:pb-16">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 flex flex-col sm:flex-row items-center justify-center text-center sm:text-left gap-4 sm:gap-6">
          <svg
            className="w-8 h-8 text-[#e32b22] flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <p className="text-slate-500 text-sm max-w-2xl">
            USACarBooking.com is an independent service provider and not the
            official website or customer support channel of any car rental
            company.
          </p>
        </div>
      </section>
    </main>
  );
};

export default About;
